/*
 *  			'License for ONE AXA SOFTWARE - Inner Source'
 * 					L O A S - I S
 * 
 * The following License aims to be applied between the AXA Companies, according to the following definition:
 * "AXA Companies" means AXA, Société Anonyme with a “AXA Companies ” Board of Directors having its principal offices at 25, avenue Matignon, 75008 Paris, registered on the Commercial Registry of Paris under the number 572 093 920; and (i) any other company controlled by, or controlling AXA, with a company being considered as controlling another: (a) when it holds directly or indirectly a portion of the capital according to it the majority of the voting rights in general meetings of shareholders of this company; (b) when it holds solely the majority of the voting rights in this company by virtue of an agreement concluded with other partners or shareholders and which is not contrary to the interest of the company; (c) when it determines de facto, by voting rights which it holds, the decisions in the general meetings of shareholders of this company; (d) in any event, when it holds, directly or indirectly, a portion of voting rights greater than 40% and when no other partner or shareholder holds directly or indirectly a portion which is greater than its own; (ii) any economic interest group in which AXA and/or one or more other Companies of the AXA Group participates for at least 50% in operating costs; (iii) in the cases where the law applicable to a company limits voting rights or control (such as defined here in above), this company will be deemed to be a company of the AXA Group, if the voting rights in 2 Binding Corporate Rules – AXA Group – version 2013 general shareholders’ meetings or the control held by a Company of the AXA Group reaches the maximum amount fixed by said applicable law; and (iv) all AXA Companies constitute the “AXA Group” (hereafter "One AXA").  (Binding Corporate Rules - AXA Group - version 2019).
 * 
 * Software credits: 
 * 	Creator: AXA GO GETD
 * 	Main contributors: AXA GO GETD
 * 
 * Permission is hereby granted, free of charge, to any person or entity within One AXA obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software within One AXA, including the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above credit notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * Any improvement or modification of the Software shall be made available to the whole One AXA, under the terms of this License.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Table, Space, Button, Popconfirm } from 'antd';

import { setIntent } from '@store/reducers/intents';
import { usePaginationWrapper } from '@hooks/usePaginationWrapper';
import { formatDateTimeNoSeconds } from '@utils/format/dates';
import { MESSAGES } from '@translations/labels';
import SVGIconEdit from '@svg/IconEdit';
import SVGIconDelete from '@svg/IconDelete';
import SvgIconEye from '@svg/IconEye';
import { idField } from '@constants';
import { useDeleteFlow } from './useDeleteFlow';
import { useFlowErrorsVerify } from '../../hooks/useFlowErrorsVerify';
import { ExclamationCircleFilled } from '@ant-design/icons';

const FlowsTable = ({ flows, versionId }) => {
  const dispatch = useDispatch();
  const labels = { ...MESSAGES.manageBot };
  const { paginationWrapper } = usePaginationWrapper();
  const { deleteIntent } = useDeleteFlow();
  const { errors } = useFlowErrorsVerify();

  const handleEdit = intent => event => {
    event.preventDefault();
    dispatch(setIntent(intent[idField]));
  };
  const handleDelete = intent => event => {
    event.preventDefault();
    return deleteIntent(intent.agentId, intent[idField]);
  };
  const showFlowErrorAlert = flowName => {
    const isFlowWithErrors = errorList => {
      if (errorList) {
        for (let flowError of errorList) {
          if (flowError.data?.flows[0]?.name === flowName) {
            return true;
          }
        }
      }
      return false;
    };

    return (
      isFlowWithErrors(errors?.flows) ||
      isFlowWithErrors(errors?.actions) ||
      isFlowWithErrors(errors?.dialogs) ||
      isFlowWithErrors(errors?.cards)
    );
  };

  const columns = [
    {
      title: () => <span className="bold">{labels.tableFlowName}</span>,
      key: 'action',
      fixed: true,
      align: 'left',
      width: 200,
      render: record => {
        return (
          <Button size="small" type="ghost" onClick={handleEdit(record)} style={{ border: 'none' }}>
            {!versionId ? (
              <Link
                to={`/manage-the-bot/flows/${record[idField]}/edit`}
                className="link-secondary block flow-table-go-to-flow-button"
              >
                {showFlowErrorAlert(record.name) && (
                  <ExclamationCircleFilled
                    style={{
                      fontSize: '20px',
                      color: '#f9c22e',
                      minWidth: '40px',
                      transform: 'translate(0px, -3px)',
                    }}
                  />
                )}
                {record.name}
              </Link>
            ) : (
              <Link
                to={`/manage-the-bot/flows/${record[idField]}/view`}
                className="link-secondary block flow-table-go-to-flow-button"
              >
                {record.name}
              </Link>
            )}
          </Button>
        );
      },
    },
    {
      title: <span className="bold">Created at</span>,
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'left',
      fixed: true,
      width: 150,
      render: (record, item) => {
        return item.createdAt && formatDateTimeNoSeconds(item.createdAt);
      },
    },
    {
      title: <span className="bold">Updated at</span>,
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      align: 'left',
      fixed: true,
      width: 150,
      render: (record, item) => {
        return item.updatedAt && formatDateTimeNoSeconds(item.updatedAt);
      },
    },
    {
      title: '',
      key: 'action',
      align: 'right',
      fixed: true,
      width: 100,
      render: record => {
        return flows.length > 0 ? (
          <>
            <Space size="middle">
              <Button size="small" type="ghost" onClick={handleEdit(record)} style={{ border: 'none' }}>
                {!versionId ? (
                  <Link to={`/manage-the-bot/flows/${record[idField]}/edit`}>
                    <SVGIconEdit width="14px" height="14px" />
                  </Link>
                ) : (
                  <Link to={`/manage-the-bot/flows/${record[idField]}/view`}>
                    <SvgIconEye width="14px" height="14px" />
                  </Link>
                )}
              </Button>
              {!versionId && (
                <Popconfirm title="Sure to delete?" onConfirm={handleDelete(record)}>
                  <Button
                    size="small"
                    type="ghost"
                    icon={<SVGIconDelete width="14px" height="14px" />}
                    style={{ border: 'none' }}
                  />
                </Popconfirm>
              )}
            </Space>
          </>
        ) : null;
      },
    },
  ];

  return <Table columns={columns} dataSource={flows} rowKey="_id" pagination={paginationWrapper} />;
};

FlowsTable.propTypes = {
  flows: PropTypes.array,
  versionId: PropTypes.string,
};

export { FlowsTable };
